import React from 'react';
import Chart from "react-google-charts";
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Divider from '@mui/material/Divider';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers';
import { Button, OutlinedInput, Snackbar, Alert } from '@mui/material';
import * as ETVConstants from '../ETVConstants';

class PageHitChart extends React.Component {
  state = {
    report_type: 'by_date', // by_date or by_device
    view_mode: 'page_hits', // page_hits or qr_scans
    isLoaded: false,
    from_date: ETVConstants.createDateYearsPlus(-1),
    to_date: undefined,
    data: [["Date", "#Displays", "#QR scans"]],
    snackbarOpen: false,
    snackbarMessage: '',
  };

  componentDidMount() {
    this.loadData();
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.content_id !== prevProps.content_id ||
        this.state.report_type !== prevState.report_type ||
        this.state.from_date !== prevState.from_date ||
        this.state.to_date !== prevState.to_date) {
      this.loadData();
    }
  }

  getFromDate() {
    return this.state.from_date ? ETVConstants.formatDate(this.state.from_date) : undefined;
  }

  getToDate() {
    return this.state.to_date ? ETVConstants.formatDate(this.state.to_date) : undefined;
  }

  loadData() {
    const accessToken = localStorage.getItem("etv.access_token");
    const reqUserID = localStorage.getItem("etv.user_id");
    let url = `${ETVConstants.getServerBase()}/hit_report?content_id=${this.props.content_id}&report_item=${this.state.report_type}`;

    const params = [];
    if (this.state.from_date) params.push(`from_date=${this.getFromDate()}`);
    if (this.state.to_date) params.push(`to_date=${this.getToDate()}`);
    if (params.length > 0) url += "&" + params.join("&");

    console.log("----> contentID="+this.props.content_id);

    fetch(url, { headers: { 'user_id': reqUserID, 'access_token': accessToken } })
      .then(res => res.json())
      .then(
        (result) => {
          const data = result.data && result.data.length > 0
            ? result.data
            : [["Date", "#Displays", "#QR scans"]];
          this.setState({ data: data, isLoaded: true });
        },
        (error) => {
          console.error("Failed to load data", error);
        }
      );
  }

  setFromDate = (date) => {
    if (this.state.to_date && date && date.isAfter(this.state.to_date)) {
      this.setState({
        from_date: date,
        to_date: undefined,
        isLoaded: false,
        snackbarOpen: true,
        snackbarMessage: "End date cleared because it's earlier than start date.",
      });
    } else {
      this.setState({ from_date: date, isLoaded: false });
    }
  };

  setToDate = (date) => {
    if (this.state.from_date && date && date.isBefore(this.state.from_date)) {
      this.setState({
        snackbarOpen: true,
        snackbarMessage: "End date cannot be before start date.",
      });
      return;
    }
    this.setState({ to_date: date, isLoaded: false });
  };

  handleReportTypeChange = (e) => {
    this.setState({ report_type: e.target.value, isLoaded: false });
  };

  handleViewModeChange = (e) => {
    this.setState({ view_mode: e.target.value });
  };

  getChartData() {
    const { data, view_mode, from_date, to_date, report_type } = this.state;
  
    if (data.length <= 1) return data; // Only header
  
    let filteredData = data.slice(1);
  
    if (report_type === 'by_date') {
      const fromDateStr = from_date ? ETVConstants.formatDate(from_date) : undefined;
      const toDateStr = to_date ? ETVConstants.formatDate(to_date) : undefined;
  
      filteredData = filteredData.filter(row => {
        const dateStr = row[0];
        if (fromDateStr && dateStr < fromDateStr) return false;
        if (toDateStr && dateStr > toDateStr) return false;
        return true;
      });
    }
  
    if (view_mode === 'page_hits') {
      return [
        [data[0][0], "#Displays"], 
        ...filteredData.map(row => [row[0], row[1]])
      ];
    } else {
      return [
        [data[0][0], "#QR Scans"], 
        ...filteredData.map(row => [row[0], row[2]])
      ];
    }
  }
  

  downloadCSV = () => {
    const chartData = this.getChartData();
    if (!chartData || chartData.length <= 1) {
      console.warn("No data to download");
      return;
    }

    let csvContent = "\uFEFF"; // UTF-8 BOM
    chartData.forEach((rowArray) => {
      const row = rowArray.map(item => {
        const strItem = String(item);
        return strItem.includes(',') ? `"${strItem}"` : strItem;
      }).join(",");
      csvContent += row + "\r\n";
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    const filename = `report_${this.props.content_id}_${this.state.view_mode}_${Date.now()}.csv`;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  clearDateFilters = () => {
    this.setState({
      from_date: null,
      to_date: null,
      isLoaded: false,
    });
  };

  handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return; // still allow click outside to close
    this.setState({ snackbarOpen: false });
  };

  render() {
    const chartData = this.getChartData();
    const isEmptyData = !chartData || chartData.length <= 1;

    const chartTitle = this.state.view_mode === 'page_hits'
      ? 'Page Hits Over Time'
      : 'QR Code Scans Over Time';

    const barColor = this.state.view_mode === 'page_hits'
      ? '#4285F4'
      : '#FBBC05';

    return (
      <span>
        <div>
          {/* Report Type Selector */}
          <FormControl style={{ marginTop: 20, width: 250 }}>
            <InputLabel id="report_type_label">{ETVConstants.trans("choose_report_type")}</InputLabel>
            <Select
              labelId="report_type_label"
              fullWidth
              id="report_type"
              input={<OutlinedInput label={"content_format"} />}
              value={this.state.report_type}
              onChange={this.handleReportTypeChange}
            >
              <MenuItem value='by_date'>{ETVConstants.trans("report_by_date")}</MenuItem>
              <MenuItem value='by_device'>{ETVConstants.trans("report_by_device")}</MenuItem>
            </Select>
          </FormControl>

          {/* View Mode Selector */}
          <FormControl style={{ marginTop: 20, marginLeft: 20, width: 200 }}>
            <InputLabel id="view_mode_label">View</InputLabel>
            <Select
              labelId="view_mode_label"
              fullWidth
              id="view_mode"
              input={<OutlinedInput label={"view_mode"} />}
              value={this.state.view_mode}
              onChange={this.handleViewModeChange}
            >
              <MenuItem value='page_hits'>Page Hits</MenuItem>
              <MenuItem value='qr_scans'>QR Code Scans</MenuItem>
            </Select>
          </FormControl>

          {/* Date Range Pickers and Clear Filters */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <FormControl style={{ marginTop: 20, marginLeft: 20 }}>
              <DatePicker
                label={ETVConstants.trans("from_date")}
                value={this.state.from_date}
                format='YYYY-MM-DD'
                onChange={this.setFromDate}
              />
            </FormControl>
            <FormControl style={{ marginTop: 20, marginLeft: 20 }}>
              <DatePicker
                label={ETVConstants.trans("until_date")}
                value={this.state.to_date}
                format='YYYY-MM-DD'
                onChange={this.setToDate}
              />
            </FormControl>
            <FormControl style={{ marginTop: 20, marginLeft: 20 }}>
              <Button size='small' variant="outlined" onClick={this.clearDateFilters} style={{ height: 55 }}>
                Clear Filters
              </Button>
            </FormControl>
          </LocalizationProvider>
        </div>

        <Divider style={{ marginTop: 20 }} />

        {/* Download Button only if data exists */}
        {!isEmptyData && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
            <Button variant='contained' onClick={this.downloadCSV} style={{ padding: '10px 20px', fontSize: '16px' }}>
              Download CSV
            </Button>
          </div>
        )}

        {/* Chart or No Data */}
        <div style={{ display: 'flex', justifyContent: 'center', maxWidth: 1000, marginTop: 20 }}>
          {isEmptyData ? (
            <div style={{ marginTop: 50, fontSize: 18, color: '#888', textAlign: 'center' }}>
              📄 No data available for selected dates.
            </div>
          ) : (
            <Chart
              width={800}
              height={450}
              chartType="BarChart"
              loader={<div>{ETVConstants.trans("loading")}</div>}
              data={chartData}
              options={{
                title: chartTitle,
                titleTextStyle: {
                  fontSize: 24,
                  bold: true,
                },
                chartArea: { width: '60%' },
                hAxis: { title: '', minValue: 0 },
                vAxis: { title: '' },
                legend: { position: 'none' },
                colors: [barColor],
              }}
              legendToggle
            />
          )}
        </div>

        {/* Snackbar */}
        <Snackbar
          open={this.state.snackbarOpen}
          autoHideDuration={4000}
          onClose={this.handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={this.handleCloseSnackbar} severity="warning" sx={{ width: '100%' }}>
            {this.state.snackbarMessage}
          </Alert>
        </Snackbar>
      </span>
    );
  }
}

export default PageHitChart;
